import { supabase } from "./supabase";
import type { Database } from "@/types/supabase";
import type { ScrapingField } from "@/types/scraper";

export type Project = Database["public"]["Tables"]["scraping_projects"]["Row"];
export type Config = Database["public"]["Tables"]["scraping_configs"]["Row"];
export type Field = Database["public"]["Tables"]["scraping_fields"]["Row"];
export type Result = Database["public"]["Tables"]["scraping_results"]["Row"];
export type Log = Database["public"]["Tables"]["scraping_logs"]["Row"];

import { withErrorHandling } from "./dbErrorHandling";

export async function createProject(name: string, description?: string) {
  return withErrorHandling("createProject", async () => {
    // First check if we already have a project with this name to avoid duplicates
    const { data: existingProjects, error: queryError } = await supabase
      .from("scraping_projects")
      .select("*")
      .eq("name", name)
      .limit(1);

    if (queryError) {
      console.error("Error checking for existing projects:", queryError);
      throw queryError;
    }

    // If a project with this name already exists, return it
    if (existingProjects && existingProjects.length > 0) {
      console.log("Found existing project:", existingProjects[0]);
      return existingProjects[0];
    }

    // Otherwise create a new project
    const { data, error } = await supabase
      .from("scraping_projects")
      .insert({ name, description })
      .select()
      .single();

    if (error) {
      console.error("Error creating project:", error);
      throw error;
    }

    return data;
  });
}

export async function createConfig({
  projectId,
  url,
  enablePagination,
  paginationSelector,
  productLinkSelector,
  maxPages,
  verboseMode,
}: {
  projectId: string;
  url: string;
  enablePagination?: boolean;
  paginationSelector?: string;
  productLinkSelector?: string;
  maxPages?: number;
  verboseMode?: boolean;
}) {
  return withErrorHandling("createConfig", async () => {
    const { data, error } = await supabase
      .from("scraping_configs")
      .insert({
        project_id: projectId,
        url,
        enable_pagination: enablePagination,
        pagination_selector: paginationSelector,
        product_link_selector: productLinkSelector,
        max_pages: maxPages,
        verbose_mode: verboseMode,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  });
}

export async function createFields(projectId: string, fields: ScrapingField[]) {
  return withErrorHandling("createFields", async () => {
    // First check if fields already exist for this project
    const { data: existingFields, error: queryError } = await supabase
      .from("scraping_fields")
      .select("*")
      .eq("project_id", projectId)
      .limit(1);

    if (queryError) {
      console.error("Error checking for existing fields:", queryError);
      throw queryError;
    }

    // If fields already exist for this project, return them
    if (existingFields && existingFields.length > 0) {
      console.log("Fields already exist for project, skipping creation");
      return existingFields;
    }

    // Otherwise create new fields
    const { data, error } = await supabase
      .from("scraping_fields")
      .insert(
        fields.map((field) => ({
          project_id: projectId,
          field_id: field.id,
          label: field.label,
          description: field.description,
          is_active: field.active,
          template_id: field.templateId,
        })),
      )
      .select();

    if (error) {
      console.error("Error creating fields:", error);
      throw error;
    }

    return data;
  });
}

export async function updateFieldSelector(fieldId: string, selector: string) {
  const { data, error } = await supabase
    .from("scraping_fields")
    .update({ selector })
    .eq("id", fieldId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function createResult({
  projectId,
  configId,
  productUrl,
  fieldId,
  value,
  confidence,
}: {
  projectId: string;
  configId: string;
  productUrl: string;
  fieldId: string;
  value?: string;
  confidence?: number;
}) {
  const { data, error } = await supabase
    .from("scraping_results")
    .insert({
      project_id: projectId,
      config_id: configId,
      product_url: productUrl,
      field_id: fieldId,
      value,
      confidence,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function createLog({
  projectId,
  configId,
  status,
  message,
  progress,
  totalProgress,
}: {
  projectId: string;
  configId: string;
  status: string;
  message: string;
  progress?: number;
  totalProgress?: number;
}) {
  const { data, error } = await supabase
    .from("scraping_logs")
    .insert({
      project_id: projectId,
      config_id: configId,
      status,
      message,
      progress,
      total_progress: totalProgress,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getProjectResults(projectId: string) {
  const { data, error } = await supabase
    .from("scraping_results")
    .select("*")
    .eq("project_id", projectId);

  if (error) throw error;
  return data;
}

export async function getProjectLogs(projectId: string) {
  const { data, error } = await supabase
    .from("scraping_logs")
    .select("*")
    .eq("project_id", projectId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return data;
}
